'use client'

import React, { useMemo, useState } from "react";
import { Col, Row, Card, Button, Badge, Form, Dropdown } from "react-bootstrap";
import { useData } from "../../storage/accessors";
import { LifePart } from "../../state/LifePart";
import { Task } from "../../state/Task";
import { ChecklistItem } from "../../state/ChecklistItem";
import { Goal } from "../../state/Goal";
import { emptyChecklistItem, emptyTask } from "../../state/reducer";
import Icon from "../../components/Icon";
import { faChevronRight, faChevronDown, faCircle, faCheckCircle, faArrowUpRightFromSquare, faPlus, faList, faTrash, faChevronLeft, faCalendar, faArrowRightToBracket } from "@fortawesome/free-solid-svg-icons";
import { useNavigate, useLocation } from "react-router";
import useWheelId from "../../hooks/useWheelId";
import MonthGrid from "../../components/lifepart/MonthGrid";
import TaskModal from "../../components/TaskModal";
import { useScreenSize } from "../../contexts/screenSizeContext";

interface TaskTreeItemProps {
    task: Task;
    lifePart: LifePart;
    depth: number;
    onOpenTask: (taskUuid: string) => void;
    onDragStart: (e: React.DragEvent, taskUuid: string) => void;
    expandedTasks: Record<string, boolean>;
    onToggleExpand: (taskUuid: string) => void;
    addingSubtaskTo: string | null;
    onAddSubtask: (taskUuid: string) => void;
    onCancelAddSubtask: () => void;
    newSubtaskTitle: string;
    onNewSubtaskTitleChange: (title: string) => void;
    onCreateSubtask: (parentTask: Task) => void;
    onDeleteSubtask?: (taskUuid: string) => void;
    onOpenAddForm?: (taskUuid: string) => void;
}

const TaskTreeItem: React.FC<TaskTreeItemProps> = ({
    task,
    lifePart,
    depth,
    onOpenTask,
    onDragStart,
    expandedTasks,
    onToggleExpand,
    addingSubtaskTo,
    onAddSubtask,
    onCancelAddSubtask,
    newSubtaskTitle,
    onNewSubtaskTitleChange,
    onCreateSubtask,
    onDeleteSubtask,
    onOpenAddForm,
}) => {
    const { tasks, checklistItems } = useData();

    // Находим подзадачи - это задачи, у которых parentTask === task.uuid
    const subtasks = Object.values(tasks.objects).filter(t => t.parentTask === task.uuid);
    const isTaskExpanded = expandedTasks[task.uuid] !== false;
    const isAddingSubtask = addingSubtaskTo === task.uuid;
    const hasSubtasks = subtasks.length > 0;
    const isSubtask = task.parentTask !== undefined && task.parentTask !== "";

    // Собираем месяцы задачи и всех её подзадач (рекурсивно)
    const allMonths = useMemo(() => {
        const months = new Set<string>();
        const collect = (t: Task) => {
            if (t.month) {
                t.month.forEach(m => months.add(m));
            }
            // Рекурсивно собираем месяцы подзадач
            Object.values(tasks.objects)
                .filter(sub => sub.parentTask === t.uuid)
                .forEach(sub => collect(sub));
        };
        collect(task);
        return Array.from(months).sort();
    }, [task, tasks.objects]);

    // Форматируем месяцы в компактный вид (фев, апр-июн, сен)
    const formattedMonths = useMemo(() => {
        if (allMonths.length === 0) return "";

        const monthNames = ["янв", "фев", "мар", "апр", "май", "июн", "июл", "авг", "сен", "окт", "ноя", "дек"];
        const currentYear = new Date().getFullYear();

        // Преобразуем YYYY-MM в объект { year, month }
        const monthToObj = (m: string) => {
            const parts = m.split("-");
            const year = parseInt(parts[0], 10);
            const month = parseInt(parts[1], 10) - 1; // 0-based индекс
            return { year, month, original: m };
        };

        // Сортируем месяцы по году и месяцу
        const sorted = [...allMonths]
            .map(monthToObj)
            .sort((a, b) => a.year - b.year || a.month - b.month);

        const ranges: string[] = [];
        let start = sorted[0];
        let end = sorted[0];

        for (let i = 1; i < sorted.length; i++) {
            const current = sorted[i];
            const isNextMonth = current.year === end.year && current.month === end.month + 1;

            if (isNextMonth) {
                // Последовательный месяц (в том же году или переход через год)
                end = current;
            } else {
                // Разрыв - сохраняем диапазон
                if (start === end) {
                    // Одиночный месяц
                    if (start.year !== currentYear) {
                        ranges.push(`${monthNames[start.month]} ${String(start.year).slice(-2)}`);
                    } else {
                        ranges.push(monthNames[start.month]);
                    }
                } else {
                    // Диапазон
                    const startStr = start.year !== currentYear
                        ? `${monthNames[start.month]} ${String(start.year).slice(-2)}`
                        : monthNames[start.month];
                    const endStr = end.year !== currentYear
                        ? `${monthNames[end.month]} ${String(end.year).slice(-2)}`
                        : monthNames[end.month];
                    ranges.push(`${startStr}-${endStr}`);
                }
                start = current;
                end = current;
            }
        }

        // Добавляем последний диапазон
        if (start === end) {
            if (start.year !== currentYear) {
                ranges.push(`${monthNames[start.month]} ${String(start.year).slice(-2)}`);
            } else {
                ranges.push(monthNames[start.month]);
            }
        } else {
            const startStr = start.year !== currentYear
                ? `${monthNames[start.month]} ${String(start.year).slice(-2)}`
                : monthNames[start.month];
            const endStr = end.year !== currentYear
                ? `${monthNames[end.month]} ${String(end.year).slice(-2)}`
                : monthNames[end.month];
            ranges.push(`${startStr}-${endStr}`);
        }

        return ranges.join(", ");
    }, [allMonths]);

    const handleDragStart = (e: React.DragEvent) => {
        e.dataTransfer.effectAllowed = "move";
        e.dataTransfer.setData("text/plain", task.uuid);
    };

    return (
        <div>
            <div
                className="d-flex align-items-center gap-2 p-2 border rounded"
                style={{
                    backgroundColor: task.isDone ? "rgba(0,0,0,0.02)" : "transparent",
                    borderColor: task.isDone ? "#dee2e6" : lifePart.color + "40",
                    opacity: task.isDone ? 0.6 : 1,
                    cursor: task.isDone ? "default" : "grab",
                    marginLeft: depth > 0 ? `${depth * 16}px` : 0,
                }}
                draggable={!task.isDone}
                onDragStart={!task.isDone ? handleDragStart : undefined}
                onClick={() => onOpenTask(task.uuid)}
            >
                <Icon
                    icon={task.isDone ? faCheckCircle : faCircle}
                    style={{
                        color: task.isDone ? "#198754" : "#6c757d",
                        fontSize: depth > 0 ? "0.75rem" : "0.85rem",
                    }}
                />
                {depth > 0 && (
                    <Icon
                        icon={faList}
                        style={{
                            color: "#6c757d",
                            fontSize: "0.7rem",
                        }}
                    />
                )}
                <span
                    className="flex-grow-1"
                    style={{
                        textDecoration: task.isDone ? "line-through" : "none",
                        color: task.isDone ? "#6c757d" : "inherit",
                        fontSize: depth > 0 ? "0.85rem" : "0.9rem",
                    }}
                >
                    {task.description}
                </span>
                {formattedMonths && (
                    <span
                        className="text-muted ms-2"
                        style={{ fontSize: depth > 0 ? "0.7rem" : "0.75rem" }}
                    >
                        {formattedMonths}
                    </span>
                )}
                <Button
                    variant="transparent"
                    size="sm"
                    className="p-0"
                    onClick={(e) => {
                        e.stopPropagation();
                        if (onOpenAddForm) {
                            onOpenAddForm(task.uuid);
                        } else {
                            onAddSubtask(task.uuid);
                        }
                    }}
                    style={{
                        color: lifePart.color,
                        fontSize: depth > 0 ? "0.75rem" : "0.85rem",
                    }}
                    title="Добавить подзадачу"
                >
                    <Icon icon={faPlus} />
                </Button>
                {isSubtask && onDeleteSubtask && (
                    <Button
                        variant="transparent"
                        size="sm"
                        className="p-0"
                        onClick={(e) => {
                            e.stopPropagation();
                            if (confirm("Удалить подзадачу?")) {
                                onDeleteSubtask(task.uuid);
                            }
                        }}
                        style={{
                            color: "#dc3545",
                            fontSize: depth > 0 ? "0.75rem" : "0.85rem",
                        }}
                        title="Удалить подзадачу"
                    >
                        <Icon icon={faTrash} />
                    </Button>
                )}
            </div>

            {subtasks.length > 0 && isTaskExpanded && (
                <div>
                    {subtasks.map((subtask) => (
                        <TaskTreeItem
                            key={subtask.uuid}
                            task={subtask}
                            lifePart={lifePart}
                            depth={depth + 1}
                            onOpenTask={onOpenTask}
                            onDragStart={onDragStart}
                            expandedTasks={expandedTasks}
                            onToggleExpand={onToggleExpand}
                            addingSubtaskTo={addingSubtaskTo}
                            onAddSubtask={onAddSubtask}
                            onCancelAddSubtask={onCancelAddSubtask}
                            newSubtaskTitle={newSubtaskTitle}
                            onNewSubtaskTitleChange={onNewSubtaskTitleChange}
                            onCreateSubtask={onCreateSubtask}
                            onDeleteSubtask={onDeleteSubtask}
                            onOpenAddForm={onOpenAddForm}
                        />
                    ))}
                </div>
            )}

            {isAddingSubtask && (
                <div className="ms-4 mt-2 d-flex align-items-center gap-2">
                    <Icon
                        icon={faList}
                        style={{
                            color: "#6c757d",
                            fontSize: "0.7rem",
                        }}
                    />
                    <Form.Control
                        type="text"
                        size="sm"
                        placeholder="Название подзадачи..."
                        value={newSubtaskTitle}
                        onChange={(e) => onNewSubtaskTitleChange(e.target.value)}
                        onKeyDown={(e) => {
                            if (e.key === "Enter" && newSubtaskTitle.trim()) {
                                onCreateSubtask(task);
                            } else if (e.key === "Escape") {
                                onCancelAddSubtask();
                            }
                        }}
                        autoFocus
                        onClick={(e) => e.stopPropagation()}
                    />
                    <Button
                        variant="success"
                        size="sm"
                        onClick={(e) => {
                            e.stopPropagation();
                            if (newSubtaskTitle.trim()) {
                                onCreateSubtask(task);
                            }
                        }}
                        disabled={!newSubtaskTitle.trim()}
                    >
                        <Icon icon={faPlus} />
                    </Button>
                    <Button
                        variant="outline-secondary"
                        size="sm"
                        onClick={(e) => {
                            e.stopPropagation();
                            onCancelAddSubtask();
                        }}
                    >
                        Отмена
                    </Button>
                </div>
            )}
        </div>
    );
};

export default function PlanPage() {
    const wheelId = useWheelId();
    const navigate = useNavigate();
    const location = useLocation();
    const { roles, tasks, plans, trackers, checklistItems } = useData();
    const screenSize = useScreenSize();
    const isMobile = screenSize === "MOBILE";
    const [selectedTaskUuid, setSelectedTaskUuid] = useState<string | null>(null);
    const [selectedLifePartUuid, setSelectedLifePartUuid] = useState<string>("");
    const [expandedLifeParts, setExpandedLifeParts] = useState<Record<string, boolean>>({});
    const [expandedTasks, setExpandedTasks] = useState<Record<string, boolean>>({});
    const [addingSubtaskTo, setAddingSubtaskTo] = useState<string | null>(null);
    const [newSubtaskTitle, setNewSubtaskTitle] = useState("");

    // Для мобильной карусели
    const [currentLifePartIndex, setCurrentLifePartIndex] = useState<number>(0);
    const [currentStepIndex, setCurrentStepIndex] = useState<number>(0);
    const [selectedMonth, setSelectedMonth] = useState<string | null>(null);

    // Читаем query-параметр lifepart для установки текущей сферы
    React.useEffect(() => {
        const params = new URLSearchParams(location.search);
        const lifepartUuid = params.get('lifepart');
        if (lifepartUuid) {
            const index = sortedRoles.findIndex(r => r.uuid === lifepartUuid);
            if (index !== -1) {
                setCurrentLifePartIndex(index);
                setSelectedLifePartUuid(lifepartUuid);
            }
            // Очищаем параметр из URL
            params.delete('lifepart');
            navigate({ search: params.toString() }, { replace: true });
        }
    }, [location.search]);

    // Сбрасываем индекс шага при смене сферы жизни
    React.useEffect(() => {
        setCurrentStepIndex(0);
    }, [currentLifePartIndex]);

    // Сортируем LifePart по order (так же как на странице roles)
    const sortedRoles: LifePart[] = useMemo(() => {
        return [...Object.values(roles.objects)].sort((a, b) => (a.order || 0) - (b.order || 0));
    }, [roles.objects]);

    // Группируем задачи по LifePart и шагам (forPoint)
    const tasksByLifePartAndPoint = useMemo(() => {
        const result: Record<string, Record<number, Task[]>> = {};

        sortedRoles.forEach((role) => {
            result[role.uuid] = {};
            // Инициализируем шаги от 1 до 10
            for (let i = 1; i <= 10; i++) {
                result[role.uuid][i] = [];
            }
        });

        Object.values(tasks.objects).forEach((task: Task) => {
            // Пропускаем подзадачи — они отображаются внутри родительской задачи
            if (task.parentTask) return;
            if (task.forPoint != null && task.forPoint !== undefined) {
                const point = task.forPoint;
                if (point >= 1 && point <= 10 && task.lifepart) {
                    if (!result[task.lifepart]) {
                        result[task.lifepart] = {};
                        for (let i = 1; i <= 10; i++) {
                            result[task.lifepart][i] = [];
                        }
                    }
                    if (!result[task.lifepart][point]) {
                        result[task.lifepart][point] = [];
                    }
                    result[task.lifepart][point].push(task);
                }
            }
        });

        // Сортируем задачи внутри каждого шага по order
        Object.keys(result).forEach((lifepartUuid) => {
            Object.keys(result[lifepartUuid]).forEach((pointStr) => {
                const point = parseInt(pointStr, 10);
                result[lifepartUuid][point].sort((a, b) => (a.order || 0) - (b.order || 0));
            });
        });

        return result;
    }, [tasks.objects, sortedRoles]);

    const handleTaskMonthUpdate = (taskUuid: string, month: string[]) => {
        const task = tasks.objects[taskUuid];
        if (task) {
            tasks.save(taskUuid, { month });
        }
    };

    const handleOpenTask = (taskUuid: string) => {
        setSelectedTaskUuid(taskUuid);
    };

    const handleCloseTaskModal = () => {
        setSelectedTaskUuid(null);
    };

    // Инициализируем selectedLifePartUuid первым LifePart при загрузке
    React.useEffect(() => {
        if (sortedRoles.length > 0 && !selectedLifePartUuid) {
            setSelectedLifePartUuid(sortedRoles[0].uuid);
        }
    }, [sortedRoles, selectedLifePartUuid]);

    // Разворачиваем все LifePart по умолчанию
    React.useEffect(() => {
        const initialExpanded: Record<string, boolean> = {};
        sortedRoles.forEach((role) => {
            initialExpanded[role.uuid] = true;
        });
        setExpandedLifeParts(initialExpanded);
    }, [sortedRoles]);

    const toggleExpand = (lifePartUuid: string) => {
        setExpandedLifeParts((prev) => ({
            ...prev,
            [lifePartUuid]: !prev[lifePartUuid],
        }));
    };

    // Drag & Drop между шагами
    const [draggedTaskUuid, setDraggedTaskUuid] = useState<string | null>(null);
    const [dragOverPoint, setDragOverPoint] = useState<{ lifepart: string; point: number } | null>(null);

    const handleTaskDragStart = (e: React.DragEvent, taskUuid: string) => {
        setDraggedTaskUuid(taskUuid);
        e.dataTransfer.effectAllowed = "move";
        e.dataTransfer.setData("text/plain", taskUuid);
    };

    const handleDragOver = (e: React.DragEvent) => {
        e.preventDefault();
        e.dataTransfer.dropEffect = "move";
    };

    const handleDragEnter = (e: React.DragEvent, lifepart: string, point: number) => {
        e.preventDefault();
        setDragOverPoint({ lifepart, point });
    };

    const handleDragLeave = () => {
        setDragOverPoint(null);
    };

    const handleDropOnPoint = (e: React.DragEvent, lifepart: string, point: number) => {
        e.preventDefault();
        setDragOverPoint(null);
        const taskUuid = e.dataTransfer.getData("text/plain") || draggedTaskUuid;
        if (!taskUuid) return;

        const task = tasks.objects[taskUuid];
        if (!task) {
            setDraggedTaskUuid(null);
            return;
        }

        // Нельзя перетаскивать задачи из календаря (у которых есть month) в шаги
        if (task.month && task.month.length > 0) {
            setDraggedTaskUuid(null);
            return;
        }

        // Обновляем forPoint задачи
        tasks.save(taskUuid, { forPoint: point, lifepart });
        setDraggedTaskUuid(null);
    };

    const handleAddSubtask = (parentTask: Task) => {
        const title = newSubtaskTitle.trim() || "Новая подзадача";

        // 1. Создаем элемент чеклиста с названием
        const newItem = emptyChecklistItem(parentTask.uuid);
        const existingItems = Object.values(checklistItems.objects).filter(i => i.task === parentTask.uuid);
        newItem.order = existingItems.length;
        newItem.title = title;


        // 2. Создаем подзадачу
        const newTask = emptyTask(parentTask.lifepart, title, parentTask.order ? parentTask.order + 1 : parentTask.order);
        newItem.innerTask = newTask.uuid;
        checklistItems.create(newItem);
        newTask.parentTask = parentTask.uuid;
        newTask.parentChecklistItem = newItem.uuid;
        newTask.forPoint = parentTask.forPoint;
        newTask.lifepart = parentTask.lifepart;
        tasks.create(newTask);

        // Сбрасываем только текст, но оставляем режим добавления активным
        setNewSubtaskTitle("");
    };

    const handleCancelAddSubtask = () => {
        setAddingSubtaskTo(null);
        setNewSubtaskTitle("");
    };

    const handleToggleTaskExpand = (taskUuid: string) => {
        setExpandedTasks((prev) => ({
            ...prev,
            [taskUuid]: !prev[taskUuid],
        }));
    };

    // Удаляет подзадачу и очищает связь в checklistItem
    const handleDeleteSubtask = (taskUuid: string) => {
        const task = tasks.objects[taskUuid];
        if (!task) return;

        // Если у задачи есть parentChecklistItem, очищаем связь в checklistItem
        if (task.parentChecklistItem) {
            const checklistItem = checklistItems.objects[task.parentChecklistItem];
            if (checklistItem) {
                checklistItems.save(task.parentChecklistItem, { innerTask: "" });
            }
        }

        // Очищаем связи в самой задаче
        tasks.save(taskUuid, {
            parentTask: "",
            parentChecklistItem: ""
        });

        // Удаляем задачу
        tasks.delete(taskUuid);
    };

    return (
        <>
            <Row className="mt-4">
                <Col xs={12} className="mb-4">
                    <h2>План</h2>
                    <p className="text-muted">Планируйте шаги и задачи по сферам жизни</p>
                </Col>
            </Row>

            {/* Мобильная версия: два блока по 50% высоты */}
            {isMobile ? (
                <Row className="g-0" style={{ height: "calc(100vh - 120px)" }}>
                    {/* Верхний блок: одна сфера с каруселью шагов */}
                    <Col xs={12} style={{ height: "50%", overflowY: "auto" }}>
                        {sortedRoles.length === 0 ? (
                            <Card className="shadow-sm h-100">
                                <Card.Body className="text-center py-5 d-flex flex-column justify-content-center">
                                    <p className="text-muted mb-0">
                                        У вас еще нет сфер жизни. Создайте их на странице <a href={`/${wheelId}/roles`}>Баланс</a>
                                    </p>
                                </Card.Body>
                            </Card>
                        ) : (
                            (() => {
                                const lifePart = sortedRoles[currentLifePartIndex];
                                if (!lifePart) return null;

                                const points = tasksByLifePartAndPoint[lifePart.uuid] || {};
                                const isSelected = lifePart.uuid === selectedLifePartUuid;
                                const isExpanded = expandedLifeParts[lifePart.uuid] !== false;
                                const stepArrays = Object.entries(points)
                                    .filter(([_, tasksArray]) => tasksArray.length > 0)
                                    .filter(([pointStr]) => parseInt(pointStr, 10) > (lifePart.startpoint || 1))
                                    .map(([pointStr, tasksArray]) => ({
                                        point: parseInt(pointStr, 10),
                                        tasks: tasksArray
                                    }))
                                    .sort((a, b) => a.point - b.point);

                                // Корректируем индекс шага, если он выходит за границы
                                const safeStepIndex = Math.min(currentStepIndex, Math.max(0, stepArrays.length - 1));

                                return (
                                    <Card
                                        className="shadow-sm h-100"
                                        style={{
                                            borderColor: isSelected ? lifePart.color : undefined,
                                            borderWidth: isSelected ? 2 : undefined,
                                        }}
                                    >
                                        <Card.Header
                                            className="d-flex align-items-center gap-2"
                                            style={{
                                                backgroundColor: isSelected ? lifePart.color + "40" : lifePart.color + "20",
                                                borderLeft: `4px solid ${lifePart.color}`,
                                            }}
                                        >
                                            {/* Переключение сфер */}
                                            <Button
                                                variant="outline-secondary"
                                                size="sm"
                                                onClick={() => {
                                                    setCurrentLifePartIndex(prev => Math.max(0, prev - 1));
                                                    setCurrentStepIndex(0);
                                                }}
                                                disabled={currentLifePartIndex === 0}
                                            >
                                                <Icon icon={faChevronLeft} />
                                            </Button>
                                            <span
                                                className="fw-bold"
                                                style={{
                                                    flex: 1,
                                                    textAlign: "center",
                                                    color: isSelected ? lifePart.color : undefined,
                                                }}
                                            >
                                                {lifePart.name} ({currentLifePartIndex + 1}/{sortedRoles.length})
                                            </span>
                                            <Button
                                                variant="outline-secondary"
                                                size="sm"
                                                onClick={() => {
                                                    setCurrentLifePartIndex(prev => Math.min(sortedRoles.length - 1, prev + 1));
                                                    setCurrentStepIndex(0);
                                                }}
                                                disabled={currentLifePartIndex === sortedRoles.length - 1}
                                            >
                                                <Icon icon={faChevronRight} />
                                            </Button>
                                        </Card.Header>
                                        <Card.Body className="p-2" style={{ overflowY: "auto" }}>
                                            {stepArrays.length === 0 ? (
                                                <p className="text-muted text-center py-3">Нет задач для этой сферы</p>
                                            ) : (
                                                <>
                                                    {/* Карусель шагов */}
                                                    <div className="d-flex justify-content-between align-items-center mb-3">
                                                        <Button
                                                            variant="outline-secondary"
                                                            size="sm"
                                                            onClick={() => setCurrentStepIndex(prev => Math.max(0, prev - 1))}
                                                            disabled={safeStepIndex === 0}
                                                        >
                                                            <Icon icon={faChevronLeft} />
                                                        </Button>
                                                        <div className="text-center">
                                                            <span className="text-muted" style={{ fontSize: "0.85rem" }}>
                                                                Шаг {stepArrays[safeStepIndex]?.point}
                                                            </span>
                                                            <div className="fw-semibold" style={{ fontSize: "0.9rem" }}>
                                                                {lifePart.stepNames?.[String(stepArrays[safeStepIndex]?.point)] || `Шаг ${stepArrays[safeStepIndex]?.point}`}
                                                            </div>
                                                        </div>
                                                        <Button
                                                            variant="outline-secondary"
                                                            size="sm"
                                                            onClick={() => setCurrentStepIndex(prev => Math.min(stepArrays.length - 1, prev + 1))}
                                                            disabled={safeStepIndex === stepArrays.length - 1}
                                                        >
                                                            <Icon icon={faChevronRight} />
                                                        </Button>
                                                    </div>

                                                    {/* Текущий шаг */}
                                                    {(() => {
                                                        const stepData = stepArrays[safeStepIndex];
                                                        if (!stepData) return null;
                                                        const isCompleted = stepData.point <= (lifePart.startpoint || 1);

                                                        return (
                                                            <div>
                                                                <div className="d-flex align-items-center gap-2 mb-3">
                                                                    <Icon
                                                                        icon={isCompleted ? faCheckCircle : faCircle}
                                                                        style={{
                                                                            color: isCompleted ? lifePart.color : "#ccc",
                                                                            fontSize: "1rem",
                                                                        }}
                                                                    />
                                                                    <span
                                                                        className="fw-semibold"
                                                                        style={{
                                                                            color: isCompleted ? lifePart.color : "#6c757d",
                                                                            textDecoration: isCompleted ? "line-through" : "none",
                                                                        }}
                                                                    >
                                                                        Результаты:
                                                                    </span>
                                                                    <Badge bg={isCompleted ? "success" : "secondary"} pill>
                                                                        {stepData.tasks.filter(t => t.isDone).length} / {stepData.tasks.length}
                                                                    </Badge>
                                                                </div>
                                                                <div className="d-flex flex-column gap-2">
                                                                    {stepData.tasks.map((task) => (
                                                                        <TaskTreeItem
                                                                            key={task.uuid}
                                                                            task={task}
                                                                            lifePart={lifePart}
                                                                            depth={0}
                                                                            onOpenTask={handleOpenTask}
                                                                            onDragStart={handleTaskDragStart}
                                                                            expandedTasks={expandedTasks}
                                                                            onToggleExpand={handleToggleTaskExpand}
                                                                            addingSubtaskTo={addingSubtaskTo}
                                                                            onAddSubtask={handleCancelAddSubtask}
                                                                            onCancelAddSubtask={handleCancelAddSubtask}
                                                                            newSubtaskTitle={newSubtaskTitle}
                                                                            onNewSubtaskTitleChange={setNewSubtaskTitle}
                                                                            onCreateSubtask={handleAddSubtask}
                                                                            onDeleteSubtask={handleDeleteSubtask}
                                                                            onOpenAddForm={(taskUuid: string) => {
                                                                                setAddingSubtaskTo(taskUuid);
                                                                                setNewSubtaskTitle("");
                                                                                setExpandedTasks((prev) => ({
                                                                                    ...prev,
                                                                                    [taskUuid]: true,
                                                                                }));
                                                                            }}
                                                                        />
                                                                    ))}
                                                                </div>
                                                            </div>
                                                        );
                                                    })()}
                                                </>
                                            )}
                                            {/* Кнопка "Открыть роль" всегда показывается в конце */}
                                            <div className="mt-3 pt-3 border-top">
                                                <Button
                                                    variant="outline-primary"
                                                    size="sm"
                                                    className="w-100"
                                                    onClick={(e) => {
                                                        e.stopPropagation();
                                                        navigate(`/${wheelId}/roles/${lifePart.uuid}`);
                                                    }}
                                                    style={{
                                                        borderColor: lifePart.color,
                                                        color: lifePart.color,
                                                    }}
                                                >
                                                    <Icon icon={faArrowUpRightFromSquare} className="me-2" />
                                                    Открыть {lifePart.name}
                                                </Button>
                                            </div>
                                        </Card.Body>
                                    </Card>
                                );
                            })()
                        )}
                    </Col>

                    {/* Нижний блок: календарь */}
                    <Col xs={12} style={{ height: "50%", overflowY: "auto" }}>
                        {sortedRoles.length > 0 ? (
                            (() => {
                                const mobileLifePartUuid = sortedRoles[currentLifePartIndex]?.uuid;
                                return (
                                    <MonthGrid
                                        lifepartUuid={mobileLifePartUuid}
                                        tasks={tasks.objects}
                                        roles={roles}
                                        sortedRoles={sortedRoles}
                                        onTaskMonthUpdate={handleTaskMonthUpdate}
                                        onOpenTask={handleOpenTask}
                                        selectedMonth={selectedMonth}
                                        onSelectedMonthChange={setSelectedMonth}
                                        currentLifePartUuid={mobileLifePartUuid}
                                        currentStepPoint={(() => {
                                            const lifePart = sortedRoles[currentLifePartIndex];
                                            if (!lifePart) return null;
                                            const points = tasksByLifePartAndPoint[lifePart.uuid] || {};
                                            const stepArrays = Object.entries(points)
                                                .filter(([_, tasksArray]) => tasksArray.length > 0)
                                                .filter(([pointStr]) => parseInt(pointStr, 10) > (lifePart.startpoint || 1))
                                                .map(([pointStr, tasksArray]) => parseInt(pointStr, 10))
                                                .sort((a, b) => a - b);
                                            return stepArrays[currentStepIndex] || null;
                                        })()}
                                    />
                                );
                            })()
                        ) : (
                            <Card className="shadow-sm h-100">
                                <Card.Body className="text-center py-5 d-flex flex-column justify-content-center">
                                    <p className="text-muted mb-0">
                                        Создайте сферы жизни для планирования
                                    </p>
                                </Card.Body>
                            </Card>
                        )}
                    </Col>
                </Row>
            ) : (
                /* Десктопная версия */
                <Row className="g-4">
                    {/* Левая колонка: LifePart с шагами и задачами */}
                    <Col
                        lg={4}
                        xl={4}
                        style={{
                            maxHeight: "calc(100vh - 150px)",
                            overflowY: "auto",
                            paddingRight: "calc(var(--bs-gutter-x) * .5)",
                            paddingLeft: "calc(var(--bs-gutter-x) * .5)",
                        }}
                    >
                        {sortedRoles.length === 0 ? (
                            <Card className="shadow-sm">
                                <Card.Body className="text-center py-5">
                                    <p className="text-muted mb-0">
                                        У вас еще нет сфер жизни. Создайте их на странице <a href={`/${wheelId}/roles`}>Баланс</a>
                                    </p>
                                </Card.Body>
                            </Card>
                        ) : (
                            sortedRoles.map((lifePart) => {
                                const points = tasksByLifePartAndPoint[lifePart.uuid] || {};
                                const hasTasks = Object.entries(points).some(([pointStr, tasksArray]) => tasksArray.length > 0 && parseInt(pointStr, 10) > (lifePart.startpoint || 1));
                                const isSelected = lifePart.uuid === selectedLifePartUuid;
                                const isExpanded = expandedLifeParts[lifePart.uuid] !== false;

                                return (
                                    <Card
                                        key={lifePart.uuid}
                                        className="mb-4 shadow-sm"
                                        style={{
                                            borderColor: isSelected ? lifePart.color : undefined,
                                            borderWidth: isSelected ? 2 : undefined,
                                        }}
                                    >
                                        <Card.Header
                                            className="d-flex align-items-center gap-2"
                                            style={{
                                                backgroundColor: isSelected ? lifePart.color + "40" : lifePart.color + "20",
                                                borderLeft: `4px solid ${lifePart.color}`,
                                                cursor: 'pointer',
                                            }}
                                            onClick={(e) => {
                                                e.stopPropagation();
                                                setSelectedLifePartUuid(lifePart.uuid);
                                                setCurrentStepIndex(0);
                                                toggleExpand(lifePart.uuid);
                                            }}
                                        >
                                            <Icon
                                                icon={isExpanded ? faChevronDown : faChevronRight}
                                                size="sm"
                                                style={{ color: lifePart.color }}
                                                onClick={(e) => {
                                                    e.stopPropagation();
                                                    toggleExpand(lifePart.uuid);
                                                }}
                                            />
                                            <span
                                                className="fw-bold"
                                                style={{
                                                    color: isSelected ? lifePart.color : undefined,
                                                }}
                                            >
                                                {lifePart.name}
                                            </span>
                                            <Button
                                                variant="outline-secondary"
                                                size="sm"
                                                className="ms-auto"
                                                onClick={(e) => {
                                                    e.stopPropagation();
                                                    navigate(`/${wheelId}/roles/${lifePart.uuid}`);
                                                }}
                                                style={{
                                                    borderColor: lifePart.color,
                                                    color: lifePart.color,
                                                }}
                                            >
                                                <Icon icon={faArrowUpRightFromSquare} className="me-1" />
                                                Открыть
                                            </Button>

                                        </Card.Header>
                                        {isExpanded && (
                                            <Card.Body className="p-0">
                                                {/* Секция целей (Success Signs) */}
                                                {lifePart.successSigns && lifePart.successSigns.length > 0 && (
                                                    <div className="p-2" style={{ backgroundColor: lifePart.color + "08" }}>
                                                        <div className="d-flex align-items-center gap-2 mb-2 px-2">
                                                            <Icon
                                                                icon={faCheckCircle}
                                                                style={{ color: lifePart.color, fontSize: "0.9rem" }}
                                                            />
                                                            <span className="fw-semibold" style={{ color: lifePart.color }}>
                                                                Цели
                                                            </span>
                                                        </div>
                                                        <div className="ms-4 d-flex flex-column gap-1">
                                                            {lifePart.successSigns.map((sign, index) => (
                                                                <div
                                                                    key={index}
                                                                    className="d-flex align-items-center gap-2 p-2 rounded"
                                                                    style={{
                                                                        backgroundColor: sign.isDone ? "rgba(25, 135, 84, 0.05)" : "transparent",
                                                                        opacity: sign.isDone ? 0.7 : 1,
                                                                    }}
                                                                >
                                                                    <Icon
                                                                        icon={sign.isDone ? faCheckCircle : faCircle}
                                                                        style={{
                                                                            color: sign.isDone ? "#198754" : "#6c757d",
                                                                            fontSize: "0.85rem",
                                                                        }}
                                                                    />
                                                                    <span
                                                                        className="flex-grow-1"
                                                                        style={{
                                                                            textDecoration: sign.isDone ? "line-through" : "none",
                                                                            color: sign.isDone ? "#6c757d" : "inherit",
                                                                            fontSize: "0.9rem",
                                                                        }}
                                                                    >
                                                                        {sign.title}
                                                                    </span>
                                                                </div>
                                                            ))}
                                                        </div>
                                                        <hr className="my-2" />
                                                    </div>
                                                )}

                                                {!hasTasks ? (
                                                    <div
                                                        className="p-3 text-center"
                                                        onDragOver={handleDragOver}
                                                        onDragEnter={(e) => handleDragEnter(e, lifePart.uuid, 1)}
                                                        onDragLeave={handleDragLeave}
                                                        onDrop={(e) => handleDropOnPoint(e, lifePart.uuid, 1)}
                                                        style={{
                                                            backgroundColor: dragOverPoint?.lifepart === lifePart.uuid ? lifePart.color + "10" : "transparent",
                                                        }}
                                                    >
                                                        <p className="text-muted mb-2">
                                                            <small>Добавьте задачи на странице роли</small>
                                                        </p>
                                                        <Button
                                                            variant="outline-primary"
                                                            size="sm"
                                                            onClick={(e) => {
                                                                e.stopPropagation();
                                                                navigate(`/${wheelId}/roles/${lifePart.uuid}`);
                                                            }}
                                                            style={{
                                                                borderColor: lifePart.color,
                                                                color: lifePart.color,
                                                            }}
                                                        >
                                                            <Icon icon={faArrowUpRightFromSquare} className="me-1" />
                                                            Перейти к роли
                                                        </Button>
                                                    </div>
                                                ) : (
                                                    <div className="p-2">
                                                        {Object.entries(points)
                                                            .filter(([_, pointTasks]) => pointTasks.length > 0)
                                                            .filter(([pointStr]) => parseInt(pointStr, 10) > (lifePart.startpoint || 1))
                                                            .map(([pointStr, pointTasks]) => {
                                                                const point = parseInt(pointStr, 10);
                                                                const isCompleted = point <= (lifePart.startpoint || 1);
                                                                const isDragOver = dragOverPoint?.lifepart === lifePart.uuid && dragOverPoint?.point === point;

                                                                return (
                                                                    <div key={pointStr} className="mb-3">
                                                                        <div
                                                                            className="d-flex align-items-center gap-2 mb-2 p-2 rounded"
                                                                            style={{
                                                                                fontSize: "0.85rem",
                                                                                backgroundColor: isDragOver ? lifePart.color + "20" : "transparent",
                                                                                border: isDragOver ? `2px dashed ${lifePart.color}` : "none",
                                                                            }}
                                                                            onDragOver={handleDragOver}
                                                                            onDragEnter={(e) => handleDragEnter(e, lifePart.uuid, point)}
                                                                            onDragLeave={handleDragLeave}
                                                                            onDrop={(e) => handleDropOnPoint(e, lifePart.uuid, point)}
                                                                        >
                                                                            <Icon
                                                                                icon={isCompleted ? faCheckCircle : faCircle}
                                                                                style={{
                                                                                    color: isCompleted ? lifePart.color : "#ccc",
                                                                                    fontSize: "0.9rem",
                                                                                }}
                                                                            />
                                                                            <span
                                                                                className="fw-semibold"
                                                                                style={{
                                                                                    color: isCompleted ? lifePart.color : "#6c757d",
                                                                                    textDecoration: isCompleted ? "line-through" : "none",
                                                                                }}
                                                                            >
                                                                                Шаг {point}
                                                                            </span>
                                                                            <Badge bg={isCompleted ? "success" : "secondary"} pill>
                                                                                {pointTasks.filter(t => t.isDone).length} / {pointTasks.length}
                                                                            </Badge>
                                                                        </div>
                                                                        <div className="d-flex flex-column gap-1">
                                                                            {pointTasks.map((task) => (
                                                                                <TaskTreeItem
                                                                                    key={task.uuid}
                                                                                    task={task}
                                                                                    lifePart={lifePart}
                                                                                    depth={0}
                                                                                    onOpenTask={handleOpenTask}
                                                                                    onDragStart={handleTaskDragStart}
                                                                                    expandedTasks={expandedTasks}
                                                                                    onToggleExpand={handleToggleTaskExpand}
                                                                                    addingSubtaskTo={addingSubtaskTo}
                                                                                    onAddSubtask={handleCancelAddSubtask}
                                                                                    onCancelAddSubtask={handleCancelAddSubtask}
                                                                                    newSubtaskTitle={newSubtaskTitle}
                                                                                    onNewSubtaskTitleChange={setNewSubtaskTitle}
                                                                                    onCreateSubtask={handleAddSubtask}
                                                                                    onDeleteSubtask={handleDeleteSubtask}
                                                                                    onOpenAddForm={(taskUuid: string) => {
                                                                                        setAddingSubtaskTo(taskUuid);
                                                                                        setNewSubtaskTitle("");
                                                                                        setExpandedTasks((prev) => ({
                                                                                            ...prev,
                                                                                            [taskUuid]: true,
                                                                                        }));
                                                                                    }}
                                                                                />
                                                                            ))}
                                                                        </div>
                                                                    </div>
                                                                );
                                                            })}
                                                    </div>
                                                )}
                                            </Card.Body>
                                        )}
                                    </Card>
                                );
                            })
                        )}
                    </Col>

                    {/* Правая колонка: MonthGrid */}
                    <Col
                        lg={8}
                        xl={8}
                        style={{
                            position: "sticky",
                            top: "20px",
                            alignSelf: "start",
                        }}
                    >
                        {sortedRoles.length > 0 ? (
                            <MonthGrid
                                lifepartUuid={selectedLifePartUuid}
                                tasks={tasks.objects}
                                roles={roles}
                                sortedRoles={sortedRoles}
                                onTaskMonthUpdate={handleTaskMonthUpdate}
                                onOpenTask={handleOpenTask}
                            />
                        ) : (
                            <Card className="shadow-sm mt-3">
                                <Card.Body className="text-center py-5">
                                    <p className="text-muted mb-0">
                                        Создайте сферы жизни для планирования
                                    </p>
                                </Card.Body>
                            </Card>
                        )}
                    </Col>
                </Row>
            )}

            {selectedTaskUuid && (
                <TaskModal
                    taskUUID={selectedTaskUuid}
                    toggle={handleCloseTaskModal}
                />
            )}
        </>
    );
}